import { useState } from 'react';

import { ERROR_TOAST_CONFIG } from '../constants/errorToastConfig';
import type { ErrorToastCode } from '../types/errorToastConfig';
import { pollForDownloadCookie } from '../utils/cookieUtils';
import {
  CLIENT_CORRELATION_ID_HEADER,
  generateClientCorrelationId,
} from '../utils/correlationId';
import { buildDownloadUrl } from '../utils/downloadUtils';
import { capturePageAction } from '../utils/newrelic';

import { useToast } from './useToast';

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return JSON.stringify(error) ?? 'Unknown error';
};

const getFileSizeBytes = (response: Response): number | null => {
  const fileSize = response.headers.get('content-length');

  return fileSize ? parseInt(fileSize, 10) : null;
};

export const useDownload = () => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { showToast } = useToast();

  // eslint-disable-next-line max-statements
  const downloadFile = async (
    artifactKey: string,
    version: string,
  ): Promise<void> => {
    setIsDownloading(true);
    const startTime = performance.now();
    let fileSizeBytes: number | null = null;
    const clientCorrelationId = generateClientCorrelationId();

    // 1. Check if the file is available
    try {
      const headDownloadResponse = await fetch(
        buildDownloadUrl(artifactKey, version),
        {
          method: 'HEAD',
          headers: {
            [CLIENT_CORRELATION_ID_HEADER]: clientCorrelationId,
          },
        },
      );

      fileSizeBytes = headDownloadResponse.ok
        ? getFileSizeBytes(headDownloadResponse)
        : null;
      capturePageAction('head_download_response', {
        artifactKey,
        version,
        status: headDownloadResponse.ok ? 'success' : 'error',
        statusCode: headDownloadResponse.status,
        latencyMs: Math.round(performance.now() - startTime),
        fileSizeBytes,
      });

      if (!headDownloadResponse.ok) {
        const toastConfig =
          ERROR_TOAST_CONFIG[headDownloadResponse.status as ErrorToastCode] ??
          ERROR_TOAST_CONFIG.default;

        showToast(toastConfig.message);
        setIsDownloading(false);
        return;
      }
    } catch (error: unknown) {
      capturePageAction('head_download_exception', {
        artifactKey,
        version,
        latencyMs: Math.round(performance.now() - startTime),
        errorMessage: getErrorMessage(error),
      });

      showToast(ERROR_TOAST_CONFIG.default.message);
      setIsDownloading(false);
      return;
    }

    // 2. Download the file
    // L@E uses passed downloadTraceId as clientCorrelationId
    const downloadTraceId = clientCorrelationId;

    window.location.href = buildDownloadUrl(
      artifactKey,
      version,
      downloadTraceId,
    );
    setIsDownloading(false);

    // 3. Poll for the download cookie to detect when server returns the file
    pollForDownloadCookie({
      cookieName: `downloadFile_${downloadTraceId}`,
      expectedValue: 'Retrieved',
    })
      .then(() => {
        capturePageAction('get_download_file_retrieved', {
          artifactKey,
          version,
          latencyMs: Math.round(performance.now() - startTime),
          fileSizeBytes,
        });
      })
      .catch(() => {
        capturePageAction('get_download_cookie_timeout', {
          artifactKey,
          version,
        });
      });
  };

  return {
    downloadFile,
    isDownloading,
  };
};
